import React, { useState, useEffect } from 'react';
import Button from '../shared/Button';
import { trackMspCalculator } from '../../utils/analytics';
import './MspCalculator.css';

function MspCalculator() {
  const [users, setUsers] = useState(25);
  const [cloudServers, setCloudServers] = useState(3);
  const [complianceLevel, setComplianceLevel] = useState('standard'); // 'standard', 'soc2_hipaa'
  const [helpdeskLevel, setHelpdeskLevel] = useState('24_7'); // 'business_hours', '24_7'

  // Pricing & SLA Calculations
  const baseRatePerUser = complianceLevel === 'soc2_hipaa' ? 145 : 110;
  const serverRate = 180;
  const helpdeskMultiplier = helpdeskLevel === '24_7' ? 1.2 : 1.0;

  const estimatedMonthly = Math.round((users * baseRatePerUser + cloudServers * serverRate) * helpdeskMultiplier);
  const traditionalCost = Math.round(users * 320 + 4500); // Cost of in-house IT team + tools
  const estimatedSavings = Math.round(((traditionalCost - estimatedMonthly) / traditionalCost) * 100);

  useEffect(() => {
    trackMspCalculator(users, cloudServers, complianceLevel, helpdeskLevel, estimatedMonthly);
  }, [users, cloudServers, complianceLevel, helpdeskLevel, estimatedMonthly]);

  const getRecommendedPlan = () => {
    if (complianceLevel === 'soc2_hipaa' || users > 50) {
      return 'Enterprise MSP & SOC Protection';
    }
    if (users > 20) {
      return 'Pro Managed IT & Cloud Ops';
    }
    return 'Essential Managed IT';
  };

  return (
    <section className="msp-calculator-section" id="msp-calculator">
      <div className="msp-calculator-inner">
        <div className="msp-calculator-header">
          <span className="msp-badge">Interactive MSP Estimator</span>
          <h2>Calculate Your Managed IT & Operational Savings</h2>
          <p>Estimate your monthly managed IT investment with 15-minute response SLA guarantees, 24/7 Helpdesk, and SOC2/HIPAA compliance support.</p>
        </div>

        <div className="msp-calculator-card">
          <div className="msp-calc-inputs">
            {/* User Headcount Slider */}
            <div className="msp-input-group">
              <div className="msp-input-label">
                <span>Team Members / Users:</span>
                <strong className="msp-value-display">{users} Users</strong>
              </div>
              <input 
                type="range" 
                min="5" 
                max="200" 
                value={users} 
                onChange={(e) => setUsers(Number(e.target.value))} 
                className="msp-slider"
              />
              <div className="msp-range-marks">
                <span>5 Users</span>
                <span>50 Users</span>
                <span>100 Users</span>
                <span>200+ Users</span>
              </div>
            </div>

            {/* Cloud Servers Slider */}
            <div className="msp-input-group">
              <div className="msp-input-label">
                <span>Cloud & On-Prem Servers:</span>
                <strong className="msp-value-display">{cloudServers} Servers</strong>
              </div>
              <input 
                type="range" 
                min="0" 
                max="25" 
                value={cloudServers} 
                onChange={(e) => setCloudServers(Number(e.target.value))} 
                className="msp-slider"
              />
              <div className="msp-range-marks">
                <span>0 Servers</span>
                <span>5 Servers</span>
                <span>15 Servers</span>
                <span>25+ Servers</span>
              </div>
            </div>

            {/* Compliance Selection */}
            <div className="msp-input-group">
              <label className="msp-input-title">Compliance & Security Level:</label>
              <div className="msp-toggle-grid">
                <button 
                  type="button"
                  className={`msp-toggle-btn ${complianceLevel === 'standard' ? 'active' : ''}`}
                  onClick={() => setComplianceLevel('standard')}
                >
                  <span className="toggle-title">Standard Protection</span>
                  <small>Basic RMM & Antivirus</small>
                </button>
                <button 
                  type="button"
                  className={`msp-toggle-btn ${complianceLevel === 'soc2_hipaa' ? 'active' : ''}`}
                  onClick={() => setComplianceLevel('soc2_hipaa')}
                >
                  <span className="toggle-title">🛡️ SOC2 & HIPAA Compliant</span>
                  <small>MDR, 24/7 SOC & Audit Logs</small>
                </button>
              </div>
            </div>

            {/* Helpdesk SLA Level */}
            <div className="msp-input-group">
              <label className="msp-input-title">Helpdesk Support Coverage:</label>
              <div className="msp-toggle-grid">
                <button 
                  type="button"
                  className={`msp-toggle-btn ${helpdeskLevel === 'business_hours' ? 'active' : ''}`}
                  onClick={() => setHelpdeskLevel('business_hours')}
                >
                  <span className="toggle-title">8x5 Business Hours</span>
                  <small>Std Ticket Response</small>
                </button>
                <button 
                  type="button"
                  className={`msp-toggle-btn ${helpdeskLevel === '24_7' ? 'active' : ''}`}
                  onClick={() => setHelpdeskLevel('24_7')}
                >
                  <span className="toggle-title">⚡ 24/7/365 & 15-Min SLA</span>
                  <small>Guaranteed Rapid Response</small>
                </button>
              </div>
            </div>
          </div>

          {/* Results Display Box */}
          <div className="msp-calc-output">
            <div className="msp-plan-tag">{getRecommendedPlan()}</div>
            <div className="msp-price-block">
              <span className="price-label">Estimated Monthly Retainer</span>
              <div className="price-amount">
                ${estimatedMonthly.toLocaleString()}<small>/mo</small>
              </div>
              <span className="savings-badge">🔥 Approx. {estimatedSavings}% Savings vs In-House IT</span>
            </div>

            <div className="msp-guarantees-list">
              <h4>Included SLA Guarantees:</h4>
              <ul>
                <li>⚡ <strong>15-Minute Guaranteed</strong> First Response</li>
                <li>🎧 <strong>24/7/365 Live Helpdesk</strong> & Ticket Resolution</li>
                <li>🛡️ <strong>{complianceLevel === 'soc2_hipaa' ? 'SOC2 & HIPAA Compliant Security Suite' : 'Proactive Remote Monitoring & Management'}</strong></li>
                <li>💼 <strong>Co-Managed IT & Workforce Options</strong></li>
              </ul>
            </div>

            <Button href="/contact" variant="primary" className="msp-cta-btn">
              Request Customized MSP Proposal →
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default MspCalculator;
